import type { MultiPolygon, Polygon } from "geojson";
import { IRONFLEET_BOOTH } from "../constants";

export const GPS_LOCK_KEY = "ironfleet-walker:gps-lock";
/** ~120 CAD units across a ~20 ft booth → ~20 units per meter. */
export const DEFAULT_CAD_UNITS_PER_METER = 20;
/** Surveyed WGS84 position of Ironfleet booth 952. Live GPS is measured from here. */
export const IRONFLEET_BOOTH_LAT = 36.135153;
export const IRONFLEET_BOOTH_LNG = -115.157045;

const METERS_PER_DEGREE = 111_320;

export type GpsCadLock = {
  originLat: number;
  originLng: number;
  cadX: number;
  cadY: number;
  unitsPerMeter: number;
  boothId: string;
};

export type CadPoint = { x: number; y: number };

export function metersEastNorth(
  lat: number,
  lng: number,
  originLat: number,
  originLng: number,
): { east: number; north: number } {
  const latRad = (originLat * Math.PI) / 180;
  return {
    east: (lng - originLng) * Math.cos(latRad) * METERS_PER_DEGREE,
    north: (lat - originLat) * METERS_PER_DEGREE,
  };
}

export function gpsToCad(lat: number, lng: number, lock: GpsCadLock): CadPoint {
  const { east, north } = metersEastNorth(lat, lng, lock.originLat, lock.originLng);
  return {
    x: lock.cadX + east * lock.unitsPerMeter,
    y: lock.cadY + north * lock.unitsPerMeter,
  };
}

export function featureCentroid(geometry: Polygon | MultiPolygon): CadPoint | null {
  const rings: number[][][] =
    geometry.type === "Polygon"
      ? geometry.coordinates
      : geometry.coordinates.flatMap((poly) => poly);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const ring of rings) {
    for (const pair of ring) {
      const x = pair[0];
      const y = pair[1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
}

export function createLockFromFix(lat: number, lng: number, cad: CadPoint, boothId = IRONFLEET_BOOTH): GpsCadLock {
  return {
    originLat: lat,
    originLng: lng,
    cadX: cad.x,
    cadY: cad.y,
    unitsPerMeter: DEFAULT_CAD_UNITS_PER_METER,
    boothId,
  };
}

export function loadGpsLock(): GpsCadLock | null {
  try {
    const raw = localStorage.getItem(GPS_LOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GpsCadLock>;
    if (!Number.isFinite(parsed.originLat) || !Number.isFinite(parsed.originLng)) return null;
    if (!Number.isFinite(parsed.cadX) || !Number.isFinite(parsed.cadY)) return null;
    return {
      originLat: parsed.originLat as number,
      originLng: parsed.originLng as number,
      cadX: parsed.cadX as number,
      cadY: parsed.cadY as number,
      unitsPerMeter: Number.isFinite(parsed.unitsPerMeter)
        ? (parsed.unitsPerMeter as number)
        : DEFAULT_CAD_UNITS_PER_METER,
      boothId: typeof parsed.boothId === "string" && parsed.boothId ? parsed.boothId : IRONFLEET_BOOTH,
    };
  } catch {
    return null;
  }
}

export function saveGpsLock(lock: GpsCadLock): void {
  try {
    localStorage.setItem(GPS_LOCK_KEY, JSON.stringify(lock));
  } catch {
    // Storage unavailable (private mode, etc.) — fine to no-op.
  }
}

export function clearGpsLock(): void {
  try {
    localStorage.removeItem(GPS_LOCK_KEY);
  } catch {
    // Storage unavailable — fine to no-op.
  }
}
